import Vue from 'vue'
import VueRouter from 'vue-router'
import Home from '../views/Home.vue'
import store from '@/store/index'
import { mergeGuard } from '@/router/mergeGuard'
import {
  can,
  canAssignProperties,
  canManageClients,
} from '@/composables/useGuard'

Vue.use(VueRouter)

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
  },
  {
    path: '/auth',
    name: 'Auth',
    component: () =>
      import(/* webpackChunkName: "auth" */ '../views/auth/Auth.vue'),
  },
  {
    path: '/2fa/instellen',
    name: 'TwoFactorSetup',
    component: () =>
      import(
        /* webpackChunkName: "two-factor-setup" */ '../views/auth/TwoFactorSetup.vue'
      ),
  },
  {
    path: '/2fa/verifieren',
    name: 'TwoFactorVerify',
    component: () =>
      import(
        /* webpackChunkName: "two-factor-verify" */ '../views/auth/TwoFactorVerify.vue'
      ),
  },
  {
    path: '/afgegeven-materiaal',
    name: 'IssuedMaterial',
    component: () =>
      import(
        /* webpackChunkName: "issued-material-overview" */ '../views/IssuedMaterial/Overview.vue'
      ),
  },
  {
    path: '/locaties',
    name: 'LocationOverview',
    component: () =>
      import(
        /* webpackChunkName: "location-overview" */ '../views/Location/Overview.vue'
      ),
  },
  {
    path: '/locaties/toevoegen',
    name: 'CreateLocation',
    component: () =>
      import(
        /* webpackChunkName: "location-create" */ '../views/Location/CreateLocation.vue'
      ),
  },
  {
    path: '/locaties/:id',
    name: 'LocationDetail',
    component: () =>
      import(
        /* webpackChunkName: "location-detail" */ '../views/Location/Detail.vue'
      ),
  },
  {
    path: '/locaties/:id/bewerken',
    name: 'LocationEdit',
    component: () =>
      import(
        /* webpackChunkName: "location-edit" */ '../views/Location/EditLocation.vue'
      ),
    beforeEnter: (to, from, next) => {
      if (!canManageClients() && !canAssignProperties())
        return next({ name: 'Forbidden' })

      next()
    },
  },
  {
    path: '/locaties/:id/samenvoegen',
    name: 'LocationMerge',
    component: () =>
      import(
        /* webpackChunkName: "location-merge" */ '../views/Location/Merge.vue'
      ),
    beforeEnter: mergeGuard('location'),
  },
  {
    path: '/klanten',
    name: 'Clients',
    component: () =>
      import(/* webpackChunkName: "clients" */ '../views/client/Clients.vue'),
  },
  {
    path: '/klanten/filters/:filters',
    name: 'ClientsWithFilters',
    component: () =>
      import(
        /* webpackChunkName: "clients-with-filters" */ '../views/client/Clients.vue'
      ),
  },
  {
    path: '/klanten/toevoegen',
    name: 'ClientCreate',
    component: () =>
      import(
        /* webpackChunkName: "client-create" */ '../views/client/Create.vue'
      ),
    beforeEnter: (to, from, next) => {
      if (!canManageClients()) return next({ name: 'Forbidden' })

      next()
    },
  },
  {
    path: '/klanten/locations/missing',
    name: 'ClientsWithMissingLocationsView',
    component: () =>
      import(
        /* webpackChunkName: "clients-with-missing-locations" */ '../views/client/ClientsWithMissingLocationsView.vue'
      ),
    beforeEnter: (to, from, next) => {
      if (!can('import clients')) return next({ name: 'Forbidden' })

      next()
    },
  },
  {
    path: '/klanten/import',
    name: 'ImportClientsView',
    component: () =>
      import(
        /* webpackChunkName: "client-import" */ '../views/client/ImportClientsView.vue'
      ),
    beforeEnter: (to, from, next) => {
      if (!can('import clients')) return next({ name: 'Forbidden' })

      next()
    },
  },
  {
    path: '/klanten/import/files',
    name: 'ImportClientFilesView',
    component: () =>
      import(
        /* webpackChunkName: "client-import-files" */ '../views/client/ImportClientFilesView.vue'
      ),
    beforeEnter: (to, from, next) => {
      if (!can('import clients')) return next({ name: 'Forbidden' })

      next()
    },
  },
  {
    path: '/klanten/:id/bewerken',
    name: 'ClientEdit',
    component: () =>
      import(/* webpackChunkName: "client-edit" */ '../views/client/Edit.vue'),
    beforeEnter: (to, from, next) => {
      if (!canManageClients() && !canAssignProperties())
        return next({ name: 'Forbidden' })

      next()
    },
  },
  {
    path: '/klanten/:id/samenvoegen',
    name: 'ClientMerge',
    component: () =>
      import(
        /* webpackChunkName: "client-merge" */ '../views/client/Merge.vue'
      ),
    beforeEnter: mergeGuard('client'),
  },
  {
    path: '/klanten/:id',
    name: 'ClientDetail',
    component: () =>
      import(
        /* webpackChunkName: "client-detail" */ '../views/client/ClientDetail.vue'
      ),
  },
  {
    path: '/klanten/:id/toestemming',
    name: 'ClientConsent',
    component: () =>
      import(
        /* webpackChunkName: "client-consent" */ '../views/client/ClientConsent.vue'
      ),
  },
  {
    path: '/klanten/:id/e-mailadres-toevoegen',
    name: 'AddEmail',
    component: () =>
      import(
        /* webpackChunkName: "client-add-email" */ '../views/client/ClientAddEmail.vue'
      ),
  },
  {
    path: '/klanten/:id/e-mailadres-bewerken',
    name: 'EditEmail',
    component: () =>
      import(
        /* webpackChunkName: "client-edit-email" */ '../views/client/ClientEditEmail.vue'
      ),
  },
  {
    path: '/klanten/:id/e-mailadres-toevoegen/secundair',
    name: 'AddSecondaryEmail',
    component: () =>
      import(
        /* webpackChunkName: "client-add-email" */ '../views/client/ClientAddEmail.vue'
      ),
  },
  {
    path: '/klanten/:id/e-mailadres-bewerken/secundair',
    name: 'EditSecondaryEmail',
    component: () =>
      import(
        /* webpackChunkName: "client-edit-email" */ '../views/client/ClientEditEmail.vue'
      ),
  },
  {
    path: '/klanten/:id/interacties/',
    name: 'InteractionCreate',
    component: () =>
      import(
        /* webpackChunkName: "create-interaction" */ '../views/interaction/InteractionCreate.vue'
      ),
  },
  {
    path: '/eigenschappen',
    name: 'Properties',
    component: () =>
      import(
        /* webpackChunkName: "properties" */ '../views/Properties/PropertiesOverview.vue'
      ),
  },
  {
    path: '/eigenschappen/nieuw',
    name: 'NewProperty',
    component: () =>
      import(
        /* webpackChunkName: "new-property" */ '../views/Properties/NewProperty.vue'
      ),
  },
  {
    path: '/eigenschappen/:id',
    name: 'EditProperty',
    component: () =>
      import(
        /* webpackChunkName: "edit-property" */ '../views/Properties/EditProperty.vue'
      ),
  },
  {
    path: '/producten',
    name: 'Products',
    component: () =>
      import(
        /* webpackChunkName: "products" */ '../views/product/Products.vue'
      ),
  },
  {
    path: '/producten/nieuw',
    name: 'CreateProduct',
    component: () =>
      import(
        /* webpackChunkName: "create-product" */ '../views/product/CreateProduct.vue'
      ),
  },
  {
    path: '/producten/:id/bewerk',
    name: 'EditProduct',
    component: () =>
      import(
        /* webpackChunkName: "edit-product" */ '../views/product/EditProduct.vue'
      ),
  },
  {
    path: '/interacties',
    name: 'Interactions',
    component: () =>
      import(
        /* webpackChunkName: "interactions" */ '../views/interaction/Interactions.vue'
      ),
  },
  {
    path: '/interacties/:id',
    name: 'InteractionDetail',
    component: () =>
      import(
        /* webpackChunkName: "interaction-detail" */ '../views/interaction/InteractionDetail.vue'
      ),
  },
  {
    path: '/interacties/:id/bewerk',
    name: 'UpdateInteraction',
    component: () =>
      import(
        /* webpackChunkName: "update-interaction" */ '../views/interaction/UpdateInteraction.vue'
      ),
  },
  {
    path: '/dagbesteding',
    name: 'Activities',
    component: () =>
      import(
        /* webpackChunkName: "activities" */ '../views/activity/Activities.vue'
      ),
  },
  {
    path: '/reps',
    name: 'Reps',
    component: () =>
      import(/* webpackChunkName: "reps" */ '../views/rep/Reps.vue'),
  },
  {
    path: '/reps/:id',
    name: 'RepDetails',
    component: () =>
      import(/* webpackChunkName: "show-rep" */ '../views/rep/RepDetail.vue'),
  },
  {
    path: '/dagbesteding/',
    name: 'CreateActivity',
    component: () =>
      import(
        /* webpackChunkName: "create-activity" */ '../views/activity/CreateActivity.vue'
      ),
  },
  {
    path: '/dagbesteding/:date/:id',
    name: 'ActivityDetail1',
    component: () =>
      import(
        /* webpackChunkName: "create-activity-detail" */ '../views/activity/ActivityDetail.vue'
      ),
  },
  {
    path: '/dagbesteding/:date',
    name: 'ActivityDetail2',
    component: () =>
      import(
        /* webpackChunkName: "activity-detail" */ '../views/activity/ActivityDetail.vue'
      ),
  },
  {
    path: '/dagbesteding/:id/bewerk',
    name: 'EditActivity',
    component: () =>
      import(
        /* webpackChunkName: "edit-activity" */ '../views/activity/EditActivity.vue'
      ),
  },
  {
    path: '/account',
    name: 'Account',
    component: () =>
      import(/* webpackChunkName: "account" */ '../views/Account.vue'),
  },
  {
    path: '/omnichannel/emails',
    name: 'EmailOverview',
    component: () =>
      import(
        /* webpackChunkName: "email-overview" */ '../views/Omnichannel/Email/EmailOverview.vue'
      ),
  },
  {
    path: '/omnichannel/emails/nieuw',
    name: 'EmailEditor',
    component: () =>
      import(
        /* webpackChunkName: "email-editor" */ '../views/Omnichannel/Email/EmailEditor.vue'
      ),
  },
  {
    path: '/omnichannel/emails/:id',
    name: 'EmailDetail',
    component: () =>
      import(
        /* webpackChunkName: "email-detail" */ '../views/Omnichannel/Email/EmailDetail.vue'
      ),
  },
  {
    path: '/omnichannel/email-templates',
    name: 'EmailTemplateOverview',
    component: () =>
      import(
        /* webpackChunkName: "email-template-overview" */ '../views/Omnichannel/EmailTemplate/EmailTemplateOverview.vue'
      ),
  },
  {
    path: '/omnichannel/email-templates/:id',
    name: 'EmailTemplateEditor',
    component: () =>
      import(
        /* webpackChunkName: "email-template-editor" */ '../views/Omnichannel/EmailTemplate/EmailTemplateEditor.vue'
      ),
  },
  {
    path: '/admin',
    name: 'AdminDashboard',
    component: () =>
      import(
        /* webpackChunkName: "admin-dashboard" */ '../views/admin/Dashboard.vue'
      ),
  },
  {
    path: '/admin/design-system',
    name: 'DesignSystemView',
    component: () =>
      import(
        /* webpackChunkName: "design-system" */ '../views/admin/DesignSystemView.vue'
      ),
  },
  {
    path: '/admin/gebruikers',
    name: 'Users',
    component: () =>
      import(/* webpackChunkName: "users" */ '../views/admin/Users.vue'),
  },
  {
    path: '/admin/gebruikers/nieuw',
    name: 'CreateUser',
    component: () =>
      import(/* webpackChunkName: "add-user" */ '../views/admin/AddUser.vue'),
  },
  {
    path: '/admin/gebruikers/:id',
    name: 'ShowUser',
    component: () =>
      import(/* webpackChunkName: "show-user" */ '../views/admin/ShowUser.vue'),
  },
  {
    path: '/admin/gebruikers/:id/bewerk',
    name: 'UpdateUser',
    component: () =>
      import(
        /* webpackChunkName: "update-user" */ '../views/admin/UpdateUser.vue'
      ),
  },
  {
    path: '/admin/specialismen',
    name: 'SpecialismsView',
    component: () =>
      import(
        /* webpackChunkName: "specialism-overview" */ '../views/admin/SpecialismsView.vue'
      ),
  },
  {
    path: '/admin/specialismen/nieuw',
    name: 'CreateSpecialismView',
    component: () =>
      import(
        /* webpackChunkName: "specialism-create" */ '../views/admin/CreateSpecialismView.vue'
      ),
  },
  {
    path: '/admin/specialismen/:id/bewerk',
    name: 'EditSpecialismView',
    component: () =>
      import(
        /* webpackChunkName: "specialism-edit" */ '../views/admin/EditSpecialismView.vue'
      ),
  },
  {
    path: '/admin/durations',
    name: 'DurationsView',
    component: () =>
      import(
        /* webpackChunkName: "durations-overview" */ '../views/admin/DurationsView.vue'
      ),
  },
  {
    path: '/admin/durations/new',
    name: 'CreateDurationView',
    component: () =>
      import(
        /* webpackChunkName: "durations-create" */ '../views/admin/CreateDurationView.vue'
      ),
  },
  {
    path: '/admin/durations/:id/edit',
    name: 'EditDurationView',
    component: () =>
      import(
        /* webpackChunkName: "durations-edit" */ '../views/admin/EditDurationView.vue'
      ),
  },
  {
    path: '/admin/reasons',
    name: 'ReasonsView',
    component: () =>
      import(
        /* webpackChunkName: "reasons-overview" */ '../views/admin/ReasonsView.vue'
      ),
  },
  {
    path: '/admin/reasons/new',
    name: 'CreateReasonView',
    component: () =>
      import(
        /* webpackChunkName: "reasons-create" */ '../views/admin/CreateReasonView.vue'
      ),
  },
  {
    path: '/admin/reasons/:id/edit',
    name: 'EditReasonView',
    component: () =>
      import(
        /* webpackChunkName: "reasons-edit" */ '../views/admin/EditReasonView.vue'
      ),
  },
  {
    path: '/admin/therapeutic-areas',
    name: 'TherapeuticAreasView',
    component: () =>
      import(
        /* webpackChunkName: "therapeutic-areas-overview" */ '../views/admin/TherapeuticAreasView.vue'
      ),
  },
  {
    path: '/admin/therapeutic-areas/new',
    name: 'CreateTherapeuticAreaView',
    component: () =>
      import(
        /* webpackChunkName: "therapeutic-areas-create" */ '../views/admin/CreateTherapeuticAreaView.vue'
      ),
  },
  {
    path: '/admin/therapeutic-areas/:id/edit',
    name: 'EditTherapeuticAreaView',
    component: () =>
      import(
        /* webpackChunkName: "therapeutic-areas-edit" */ '../views/admin/EditTherapeuticAreaView.vue'
      ),
  },
  {
    path: '/admin/locatietypes',
    name: 'LocationTypesView',
    component: () =>
      import(
        /* webpackChunkName: "location-types-overview" */ '../views/admin/LocationTypesView.vue'
      ),
  },
  {
    path: '/admin/locatietypes/nieuw',
    name: 'CreateLocationTypeView',
    component: () =>
      import(
        /* webpackChunkName: "location-types-create" */ '../views/admin/CreateLocationTypeView.vue'
      ),
  },
  {
    path: '/admin/locatietypes/:id/bewerk',
    name: 'EditLocationTypeView',
    component: () =>
      import(
        /* webpackChunkName: "location-types-edit" */ '../views/admin/EditLocationTypeView.vue'
      ),
  },
  {
    path: '/admin/instellingen',
    name: 'edit-settings-view',
    component: () =>
      import(
        /* webpackChunkName: "edit-settings-view" */ '../views/admin/EditSettingsView.vue'
      ),
  },
  {
    path: '/admin/settings/client',
    name: 'edit-client-settings-view',
    component: () =>
      import(
        /* webpackChunkName: "edit-client-settings-view" */ '../views/admin/EditClientSettingsView.vue'
      ),
  },
  {
    path: '/admin/settings/location',
    name: 'edit-location-settings-view',
    component: () =>
      import(
        /* webpackChunkName: "edit-location-settings-view" */ '../views/admin/EditLocationSettingsView.vue'
      ),
  },
  {
    path: '/admin/settings/interaction',
    name: 'edit-interaction-settings-view',
    component: () =>
      import(
        /* webpackChunkName: "edit-interaction-settings-view" */ '../views/admin/EditInteractionSettingsView.vue'
      ),
  },
  {
    path: '/admin/settings/issued-material',
    name: 'edit-issued-material-settings-view',
    component: () =>
      import(
        /* webpackChunkName: "edit-issued-material-settings-view" */ '../views/admin/EditIssuedMaterialSettingsView.vue'
      ),
  },
  {
    path: '/admin/settings/activity',
    name: 'edit-activity-settings-view',
    component: () =>
      import(
        /* webpackChunkName: "edit-activity-settings-view" */ '../views/admin/EditActivitySettingsView.vue'
      ),
  },
  {
    path: '/admin/settings/custom-properties',
    name: 'edit-custom-properties-settings-view',
    component: () =>
      import(
        /* webpackChunkName: "edit-custom-properties-settings-view" */ '../views/admin/EditCustomPropertiesSettingsView.vue'
      ),
  },
  {
    path: '/admin/settings/client/columns',
    name: 'client-column-settings-view',
    component: () =>
      import(
        /* webpackChunkName: "client-column-settings-view" */ '../views/admin/ClientColumnSettingsView.vue'
      ),
  },
  {
    path: '/admin/settings/location/columns',
    name: 'location-column-settings-view',
    component: () =>
      import(
        /* webpackChunkName: "location-column-settings-view" */ '../views/admin/LocationColumnSettingsView.vue'
      ),
  },
  {
    path: '/admin/settings/interaction/columns',
    name: 'interaction-column-settings-view',
    component: () =>
      import(
        /* webpackChunkName: "interaction-column-settings-view" */ '../views/admin/InteractionColumnSettingsView.vue'
      ),
  },
  {
    path: '/403',
    name: 'Forbidden',
    component: () =>
      import(
        /* webpackChunkName: "forbidden" */ '../views/error/Forbidden.vue'
      ),
  },
  {
    path: '/404',
    name: 'NotFound',
    component: () =>
      import(/* webpackChunkName: "not-found" */ '../views/error/NotFound.vue'),
  },
  {
    path: '*',
    redirect: '/404',
  },
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
})

// Signing in has three states, not two: between the password and the one-time
// code a token exists but no user does. This guard stays synchronous - which of
// the two 2FA routes applies depends on `has2FA`, and the views resolve that
// themselves rather than making the guard wait on a request.
const TWO_FACTOR_ROUTES = ['TwoFactorSetup', 'TwoFactorVerify']

router.beforeEach((to, from, next) => {
  const { authenticatedUser, accessToken } = store.state
  const isAuthRoute = to.name === 'Auth'
  const isTwoFactorRoute = TWO_FACTOR_ROUTES.includes(to.name as string)

  if (authenticatedUser) {
    // Signed in: the auth screens have nothing left to offer.
    return isAuthRoute || isTwoFactorRoute ? next({ name: 'Home' }) : next()
  }

  if (accessToken) {
    // Mid-login. Verify is the safe landing spot: if no secret is enrolled yet
    // that view redirects to the setup one on mount.
    return isTwoFactorRoute ? next() : next({ name: 'TwoFactorVerify' })
  }

  return isAuthRoute ? next() : next({ name: 'Auth' })
})

export default router
